import { Laptop } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { cn } from '../../utils/cn';
import { formatRelativeTime } from '../../utils/format';
import type { DeviceSummary, WazuhAgentStatus } from '../../types';

const statusTone: Record<WazuhAgentStatus, 'success' | 'danger' | 'warning' | 'neutral'> = {
  active: 'success',
  disconnected: 'danger',
  pending: 'warning',
  never_connected: 'neutral',
};

const statusLabel: Record<WazuhAgentStatus, string> = {
  active: 'Active',
  disconnected: 'Disconnected',
  pending: 'Pending',
  never_connected: 'Never connected',
};

const UNPATCHED_WARN_DAYS = 60;

function patchCell(device: DeviceSummary) {
  if (device.osPlatform !== 'windows') {
    return <span className="text-slate-400">n/a</span>;
  }
  if (device.daysSinceLastPatch === null) {
    return <span className="font-medium text-amber-600 dark:text-amber-400">Unknown</span>;
  }
  const overdue = device.daysSinceLastPatch > UNPATCHED_WARN_DAYS;
  return (
    <span className={cn(overdue && 'font-medium text-amber-600 dark:text-amber-400')}>
      {device.lastPatchInstalledAt ? formatRelativeTime(device.lastPatchInstalledAt) : 'Unknown'}
      {overdue && ` (${device.daysSinceLastPatch}d ago)`}
    </span>
  );
}

export function DeviceInventoryTable({ devices, loading }: { devices: DeviceSummary[]; loading?: boolean }) {
  if (!loading && devices.length === 0) {
    return (
      <EmptyState
        icon={Laptop}
        title="No devices reporting yet"
        description="Install the Wazuh agent on a laptop or workstation and it will appear here automatically."
      />
    );
  }

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-800">
            <th className="py-2.5 pr-4 font-medium">Device</th>
            <th className="py-2.5 pr-4 font-medium">Status</th>
            <th className="py-2.5 pr-4 font-medium">OS</th>
            <th className="py-2.5 pr-4 font-medium">Packages</th>
            <th className="py-2.5 pr-4 font-medium">Last patch</th>
            <th className="py-2.5 pr-4 font-medium">Last seen</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={6} className="py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                  </td>
                </tr>
              ))
            : devices.map((device) => (
                <tr key={device.agentId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3 pr-4 font-medium text-slate-800 dark:text-slate-200">{device.name}</td>
                  <td className="py-3 pr-4">
                    <Badge tone={statusTone[device.status]}>{statusLabel[device.status]}</Badge>
                  </td>
                  <td className="py-3 pr-4 text-slate-500 dark:text-slate-400">{device.osName ?? 'Unknown'}</td>
                  <td className="py-3 pr-4 text-slate-500 dark:text-slate-400">{device.packageCount ?? '-'}</td>
                  <td className="py-3 pr-4">{patchCell(device)}</td>
                  <td className="py-3 pr-4 text-slate-400">
                    {device.lastInventoryScanAt ? formatRelativeTime(device.lastInventoryScanAt) : 'Never'}
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
